/* eslint-disable camelcase */
import lodash from 'lodash';
import { Box, Button, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import { UserContext } from '../../Context/UserContext/UserContextProvider';
import {
  AllTShirtMeasuresMandatory,
  TeeMeasureLabel,
} from '../../types/databaseTables/idealteemeasures';
import { FormContainer, PageContainer } from '../../components/Containers';
import { teesPropertiesObject } from '../../tools/generalProperties';
import ErrorBoundaryWithSuspense from '../../components/ErrorBoundaryWithSuspense';
import IdealMeasureSlider from '../../components/IdealMeasureSlider';
import HelpDialog from '../../components/HelpDialog/HelpDialog';
import HelpContentsIdealMeasurements from '../../components/HelpDialog/HelpContentsIdealMeasurements';

export interface IdealTeeMeasuresLocalState extends AllTShirtMeasuresMandatory {
  idealteemeasureslog_id?: number;
  firebase_uid?: string;
  // dateadded?: string;
  updated_from?: string;
}

function InputIdealTeeMeasurements() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // USECONTEXTS
  const { userDetailsState, dispatchUserDetailsState, setOpenIdealMeasuresGeneratorState } =
    useContext(UserContext);

  // USESTATES
  const [localIdealMeasures, setLocalIdealMeasures] = useState(
    userDetailsState.idealGarmentMeasurements
  );
  const [helpSectionOpen, setHelpSectionOpen] = useState(false);

  const handleSaveChanges = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    enqueueSnackbar('Saved updated ideal t-shirt measurements locally only', {
      variant: 'success',
    });

    dispatchUserDetailsState({
      type: 'update from ideal measurements',
      payload: localIdealMeasures,
    });
  };

  useEffect(() => {
    if (lodash.isEqual(userDetailsState.idealGarmentMeasurements, localIdealMeasures)) return;
    setLocalIdealMeasures(userDetailsState.idealGarmentMeasurements);
  }, [userDetailsState.idealGarmentMeasurements]);

  function updateParentStateFunction(labelToUpdate: TeeMeasureLabel, newMeasure: number) {
    setLocalIdealMeasures((prev) => ({ ...prev, [labelToUpdate]: newMeasure }));
  }
  const updateParentState = useCallback(updateParentStateFunction, []);

  function openHelpDialogFunction(){
    setHelpSectionOpen(true)
  }

  const openHelpDialog = useCallback(openHelpDialogFunction, []);

  return (
    <PageContainer>
      <Typography variant='h3' sx={{ textAlign: 'center' }}>
        Adjust your Ideal Measurements
      </Typography>
      <HelpDialog
        headingTitle='How to adjust you ideal measurements'
        open={helpSectionOpen}
        setOpen={setHelpSectionOpen}
      >
        <HelpContentsIdealMeasurements />
      </HelpDialog>
      {/* Input Sliders */}
      <FormContainer onSubmit={handleSaveChanges}>
        {Object.keys(localIdealMeasures).map((eachMeasureKey) => (
          // Measure sliders:
          <ErrorBoundaryWithSuspense key={eachMeasureKey}>
            <IdealMeasureSlider
              eachMeasureProperties={teesPropertiesObject[eachMeasureKey as TeeMeasureLabel]}
              measureLabel={eachMeasureKey as TeeMeasureLabel}
              updateParentState={updateParentState}
              parentValue={localIdealMeasures[eachMeasureKey as TeeMeasureLabel] || 0}
              setHelpSectionOpen={openHelpDialog}
            />
          </ErrorBoundaryWithSuspense>
        ))}
        <Box
          sx={{
            width: '100%',
            marginTop: '8px',
            display: 'grid',
            gridTemplateRows: '1fr 1fr 1fr',
            justifyItems: 'center',
            '& button': { width: '300px' },
            gap: '8px 0px',
          }}
        >
          <Button color='primary' variant='contained' size='medium' type='submit'>
            Save Changes
          </Button>
          <Button
            onClick={() => setOpenIdealMeasuresGeneratorState(true)}
            color='secondary'
            variant='contained'
            size='medium'
            startIcon={<AccessibilityIcon />}
          >
            Ideal Measurements Generator
          </Button>{' '}
          <Button
            onClick={() => navigate('/')}
            color='primary'
            variant='outlined'
            size='medium'
            startIcon={<SearchIcon />}
          >
            Search For Garments
          </Button>
        </Box>
      </FormContainer>
    </PageContainer>
  );
}

export default InputIdealTeeMeasurements;
